import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ExperienceSpec } from "@luxidar/experience-schema";

import { requestPackage, requestPlan } from "./api";
import { formatBytes, prepareAsset } from "./assets";
import { Preview } from "./Preview";
import { experienceSummary } from "./summary";
import type { BuilderAsset, BuilderPhase, PlannerAssumption } from "./types";
import { usePreviewSpec } from "./usePreviewSpec";

const EXAMPLE_PROMPT = "Place this model about 1.5 metres ahead, use head tracking, rotate it slowly and play the audio when it opens.";
const INTEGRATION_SNIPPET = `import { createLuxidExperience } from "./luxid-experience.js";

const luxid = createLuxidExperience();

button.onclick = () => luxid.open();`;

type Notice = { kind: "asset" | "planner" | "clarification" | "unsupported" | "preview" | "package"; message: string };

export default function App() {
  const [phase, setPhase] = useState<BuilderPhase>("empty");
  const [assets, setAssets] = useState<BuilderAsset[]>([]);
  const [prompt, setPrompt] = useState("");
  const [spec, setSpec] = useState<ExperienceSpec | null>(null);
  const [assumptions, setAssumptions] = useState<PlannerAssumption[]>([]);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [zipBytes, setZipBytes] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const operationRef = useRef<AbortController | null>(null);
  const previewSpec = usePreviewSpec(spec, assets);
  const busy = phase === "planning" || phase === "generating-package";
  const model = assets.find((asset) => asset.type === "gltf");
  const summary = useMemo(() => spec ? experienceSummary(spec) : [], [spec]);

  useEffect(() => () => {
    operationRef.current?.abort();
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
  }, [downloadUrl]);

  const invalidateExperience = useCallback(() => {
    setSpec(null);
    setAssumptions([]);
    setNotice(null);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setZipBytes(null);
  }, [downloadUrl]);

  const chooseAsset = async (file: File | undefined, type: BuilderAsset["type"]) => {
    if (!file || busy) return;
    try {
      const next = await prepareAsset(file, type);
      invalidateExperience();
      setAssets((current) => [...current.filter((asset) => asset.type !== type), next]);
      setPhase("assets-ready");
    } catch (cause) {
      setNotice({ kind: "asset", message: cause instanceof Error ? cause.message : "This asset could not be added." });
      setPhase("error");
    }
  };

  const removeAsset = (id: string) => {
    if (busy) return;
    invalidateExperience();
    setAssets((current) => {
      const next = current.filter((asset) => asset.id !== id);
      setPhase(next.length ? "assets-ready" : "empty");
      return next;
    });
  };

  const plan = async () => {
    if (!model || !prompt.trim() || busy) return;
    setNotice(null);
    setPhase("planning");
    try {
      const result = await requestPlan(prompt.trim(), assets, spec ?? undefined);
      if (result.success) {
        setSpec(result.spec);
        setAssumptions(result.assumptions);
        if (downloadUrl) URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
        setZipBytes(null);
        setPhase("planned");
        return;
      }
      const message = result.message ?? result.error?.message ?? "The planner is temporarily unavailable.";
      if (result.status === "clarification_required") {
        setNotice({ kind: "clarification", message });
        setPhase("clarification-required");
      } else if (result.status === "unsupported_request") {
        setNotice({ kind: "unsupported", message });
        setPhase("unsupported-request");
      } else {
        setNotice({ kind: "planner", message });
        setPhase("error");
      }
    } catch {
      setNotice({ kind: "planner", message: "The planner is unavailable right now. Your assets are still here—please try again." });
      setPhase("error");
    }
  };

  const generatePackage = async () => {
    if (!spec || busy) return;
    setNotice(null);
    setPhase("generating-package");
    try {
      const archive = await requestPackage(spec, assets);
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(URL.createObjectURL(archive));
      setZipBytes(archive.size);
      setPhase("download-ready");
    } catch (cause) {
      setNotice({ kind: "package", message: cause instanceof Error ? cause.message : "Integration package could not be generated." });
      setPhase("error");
    }
  };

  const onPreviewActive = useCallback((active: boolean) => setPhase((current) => {
    if (!active) return current === "generating-package" ? current : "planned";
    return current === "generating-package" || current === "download-ready" ? current : "previewing";
  }), []);
  const onPreviewError = useCallback((message: string) => {
    setNotice({ kind: "preview", message });
    setPhase("error");
  }, []);

  const copySnippet = async () => {
    await navigator.clipboard.writeText(INTEGRATION_SNIPPET);
    setCopied(true);
    setTimeout(() => setCopied(false), 1_500);
  };

  return (
    <main className="app-shell" data-phase={phase}>
      <header className="topbar">
        <a className="brand" href="/" aria-label="Luxid Builder home"><span className="brand-mark">L</span><span>Luxid Vue</span></a>
        <span className="staging-pill">Experience Builder · Staging</span>
      </header>

      <section className="hero">
        <p className="eyebrow">Spatial experiences, without the setup</p>
        <h1>Build a Luxid Vue experience</h1>
        <p>Bring a 3D model. Describe how it should feel. Leave with a portable integration for any web project.</p>
      </section>

      <div className="workspace">
        <section className="author-panel" aria-label="Experience authoring">
          <section className="step-section" aria-labelledby="assets-heading">
            <p className="eyebrow">01 / Assets</p>
            <h2 id="assets-heading">Add what people will see</h2>
            <p className="helper">GLB works best. Add entry audio if the moment needs sound.</p>
            <div className="upload-grid">
              <label className="upload-button">
                <input data-testid="model-input" type="file" accept=".glb,.gltf,model/gltf-binary,model/gltf+json" disabled={busy} onChange={(event) => void chooseAsset(event.target.files?.[0], "gltf")} />
                <span className="upload-icon" aria-hidden="true">⌁</span>
                <span><strong>{model ? "Replace model" : "Upload 3D model"}</strong><small>GLB or self-contained GLTF</small></span>
              </label>
              <label className="upload-button secondary">
                <input data-testid="audio-input" type="file" accept=".mp3,.wav,.ogg,.m4a,.aac,.flac,audio/*" disabled={busy} onChange={(event) => void chooseAsset(event.target.files?.[0], "audio")} />
                <span className="upload-icon" aria-hidden="true">♪</span>
                <span><strong>{assets.some((asset) => asset.type === "audio") ? "Replace audio" : "Add audio"}</strong><small>Optional · plays on open</small></span>
              </label>
            </div>
            {assets.length > 0 && <ul className="asset-list" aria-label="Uploaded assets">
              {assets.map((asset) => <li key={asset.id}>
                <span className={`asset-badge ${asset.type}`}>{asset.type === "gltf" ? "3D" : "AU"}</span>
                <span><strong>{asset.displayName}</strong><small>{asset.type === "gltf" ? "3D model" : "Entry audio"} · {formatBytes(asset.file.size)}</small></span>
                <button type="button" onClick={() => removeAsset(asset.id)} disabled={busy} aria-label={`Remove ${asset.displayName}`}>Remove</button>
              </li>)}
            </ul>}
          </section>

          <section className="step-section prompt-section" aria-labelledby="prompt-heading">
            <p className="eyebrow">02 / Direction</p>
            <h2 id="prompt-heading">Describe the experience</h2>
            <label className="sr-only" htmlFor="experience-prompt">Experience description</label>
            <textarea id="experience-prompt" data-testid="prompt" value={prompt} maxLength={8000} disabled={busy} onChange={(event) => setPrompt(event.target.value)} placeholder={`Example: ${EXAMPLE_PROMPT}`} />
            <div className="prompt-footer"><span>{prompt.length.toLocaleString()} / 8,000</span><button type="button" className="text-button" onClick={() => setPrompt(EXAMPLE_PROMPT)} disabled={busy}>Use example</button></div>
            <button data-testid="plan-button" className="primary-action" type="button" disabled={!model || !prompt.trim() || busy} onClick={() => void plan()}>
              {phase === "planning" ? <><span className="spinner" /> Planning experience…</> : spec ? "Update experience" : "Generate experience"}
            </button>
            {!model && <p className="action-hint">Upload a 3D model to enable planning.</p>}
          </section>

          {notice && <aside className={`notice ${notice.kind}`} role={notice.kind === "clarification" || notice.kind === "unsupported" ? "status" : "alert"}>
            <strong>{notice.kind === "clarification" ? "One detail needed" : notice.kind === "unsupported" ? "Not supported yet" : `${notice.kind[0]!.toUpperCase()}${notice.kind.slice(1)} problem`}</strong>
            <p>{notice.message}</p>
            {notice.kind === "unsupported" && <p className="supported-list">This Builder supports 3DoF head tracking, placement, scale and rotation, auto rotation, and entry audio.</p>}
          </aside>}
        </section>

        <Preview spec={previewSpec} onActiveChange={onPreviewActive} onError={onPreviewError} />
      </div>

      {spec && <section className="result-panel" aria-labelledby="summary-heading">
        <div className="summary-copy">
          <p className="eyebrow">Your experience</p>
          <h2 id="summary-heading">{spec.experience.name}</h2>
          <ul>{summary.map((item) => <li key={item}>{item}</li>)}</ul>
          {assumptions.length > 0 && <div className="assumptions"><strong>{assumptions.length === 1 ? "Assumption" : "Assumptions"}</strong>{assumptions.map((assumption) => <p key={`${assumption.code}-${assumption.path}`}>{assumption.message}</p>)}</div>}
          <details><summary>View ExperienceSpec</summary><pre>{JSON.stringify(spec, null, 2)}</pre></details>
        </div>
        <div className="integration-card">
          <p className="eyebrow">04 / Add to your project</p>
          <h2>{downloadUrl ? "Integration ready" : "Generate integration"}</h2>
          <p>A portable module and your assets. No private registry or Luxid package install required.</p>
          {!downloadUrl && <button data-testid="generate-package" className="primary-action" type="button" disabled={busy} onClick={() => void generatePackage()}>{phase === "generating-package" ? <><span className="spinner" /> Building package…</> : "Generate integration"}</button>}
          {downloadUrl && <a data-testid="download-package" className="download-action" href={downloadUrl} download="luxid-experience.zip"><span>✓</span> Download package <small>{zipBytes === null ? "" : formatBytes(zipBytes)}</small></a>}
          <div className="snippet-heading"><span>Add to your project</span><button type="button" onClick={() => void copySnippet()}>{copied ? "Copied" : "Copy"}</button></div>
          <pre className="code-snippet"><code>{INTEGRATION_SNIPPET}</code></pre>
        </div>
      </section>}

      <footer><span>Luxid Vue Experience Builder</span><span>Assets stay in this browser until package generation.</span></footer>
    </main>
  );
}
